import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import GetUserResponse from '../../../Api/CRM/Responses/UserResponse';
import User from '../../../Api/Models/User';
import handleValidationErrors, { ValidationErrors } from '../../helpers/ResponseErrorHelper';
import useBlockingSubmit from '../../hooks/useBlockingSubmit';
import useSmartField, { FieldChangeCallback } from '../../hooks/useSmartField';

import useCountriesAndStates from 'crm/hooks/useCountriesAndStates';

export const emptyUser = new User({
    name: '',
    last_name: '',
    email: '',
    phone_country: '',
    phone: '',
    phone_confirmed: false,
    is_kyc_confirmed: false,
    password: '',
    country: '',
    state: '',
    is_reseller: false,
    reseller_wants_to_become: false,
    reseller_percent_revenue: 0,
    reseller_debit: 0,
} as GetUserResponse);

type Props = Readonly<{
    action: () => Promise<unknown>;
    user: User;
    setUser: React.Dispatch<React.SetStateAction<User>>;
}>;

function UserForm({ action, user, setUser }: Props) {
    const history = useHistory();
    const goToUserList = () => history.push('/users');
    const [countries, states] = useCountriesAndStates(user.country);
    const [errors, setErrors] = useState<ValidationErrors>({});

    const handleFieldChange: FieldChangeCallback = (name, value) =>
        setUser((prevUser) => prevUser.getNew({ [name]: value }));

    const Field = useSmartField({ errors, onChange: handleFieldChange });
    const [Submit, submitBlocked] = useBlockingSubmit();

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        try {
            await action();
            goToUserList();
        } catch (error) {
            handleValidationErrors(error, setErrors);
            submitBlocked(false);
        }
    }

    return (
        <form onSubmit={handleSubmit} autoComplete="do-not-autofill">
            <Field name="name" value={user.name} />
            <Field name="last_name" value={user.last_name} />
            <Field type="email" name="email" value={user.email} />
            <Field type="checkbox" name="is_confirmed" label="Email confirmed" value={!!user.is_confirmed} />
            <Field type="select" name="phone_country" value={user.phone_country} options={countries} />
            <Field name="phone" value={user.phone} />
            <Field type="checkbox" name="phone_confirmed" value={user.phone_confirmed} />
            <Field type="checkbox" name="is_kyc_confirmed" value={user.is_kyc_confirmed} label="KYC confirmed" />
            <Field type="password" name="password" value={user.password} disableAutofill />
            <Field type="select" name="country" value={user.country} options={countries} />
            <Field type="select" name="state" value={user.state} options={states} />
            <Field type="checkbox" name="is_reseller" value={user.is_reseller} label="Reseller" />
            <Field
                type="checkbox"
                name="reseller_wants_to_become"
                value={user.reseller_wants_to_become}
                label="Did apply for reseller"
            />
            <Field
                type="number"
                name="reseller_percent_revenue"
                value={user.reseller_percent_revenue}
                label="Reseller revenue"
            />
            <Field type="number" name="reseller_debit" value={user.reseller_debit} />
            <button type="button" onClick={goToUserList} className="btn btn-secondary">
                Cancel
            </button>
            <Submit label="Save" />
        </form>
    );
}

export default UserForm;
